import type { DriverFile } from "./ports";
import { CHUNK_SIZE } from "./chunkSize";
import { FileUnreadableError } from "./uploadChunk";
import { withPickedFileRead } from "./pickedFileReadGate";
import {
	StreamCapableDriverFile,
	type SourceReadStrategyOptions,
	type SourceReadStrategy,
	selectSourceReadStrategy
} from "./sourceReadStrategy";

export const DEFAULT_STREAM_READ_TIMEOUT_MS = 30_000;

export interface SequentialStreamPart {
	chunkIndex: number;
	PartNumber: number;
	buffer: ArrayBuffer;
}

export interface SplitDriverFileStreamOptions {
	partSize?: number;
	readTimeoutMs?: number;
	signal?: AbortSignal;
}

export interface PersistedSequentialStreamPart extends SequentialStreamPart {
	size: number;
}

export interface PersistDriverFileStreamOptions extends SplitDriverFileStreamOptions {
	persistChunk(part: PersistedSequentialStreamPart): Promise<void>;
}

type StreamReader = {
	read(): Promise<{ done: boolean; value?: unknown }>;
	cancel?(reason?: unknown): Promise<unknown>;
	releaseLock?(): void;
};

function isReadFailure(error: unknown): boolean {
	return (
		error instanceof FileUnreadableError ||
		(error instanceof Error && ["NotReadableError", "SecurityError", "NotFoundError"].includes(error.name))
	);
}

function createAbortError(): Error {
	const error = new Error("Sequential file read was cancelled");
	error.name = "AbortError";
	return error;
}

function throwIfAborted(signal: AbortSignal | undefined): void {
	if (signal?.aborted) throw createAbortError();
}

function toUint8Array(value: unknown, fileName: string): Uint8Array {
	if (value instanceof Uint8Array) return value;
	if (value instanceof ArrayBuffer) return new Uint8Array(value);
	if (ArrayBuffer.isView(value)) {
		return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
	}
	throw new FileUnreadableError(fileName);
}

async function readWithTimeout(
	reader: StreamReader,
	fileName: string,
	timeoutMs: number,
	signal: AbortSignal | undefined
): Promise<{ done: boolean; value?: unknown }> {
	throwIfAborted(signal);
	const readPromise = reader.read();
	let timer: ReturnType<typeof setTimeout> | undefined;
	let abortHandler: (() => void) | undefined;
	const timeoutPromise = new Promise<never>((_resolve, reject) => {
		timer = setTimeout(() => reject(new FileUnreadableError(fileName)), timeoutMs);
	});
	const abortPromise = new Promise<never>((_resolve, reject) => {
		if (!signal) return;
		abortHandler = () => reject(createAbortError());
		signal.addEventListener("abort", abortHandler, { once: true });
	});
	try {
		return await Promise.race([readPromise, timeoutPromise, abortPromise]);
	} catch (error) {
		// A timed-out or cancelled reader can remain pending in a browser implementation. Consume its
		// eventual rejection so the source does not produce an unhandled rejection after the caller has
		// already received the terminal result.
		void readPromise.catch(() => undefined);
		if (isReadFailure(error) || error instanceof FileUnreadableError) throw new FileUnreadableError(fileName);
		throw error;
	} finally {
		if (timer !== undefined) clearTimeout(timer);
		if (signal && abortHandler) signal.removeEventListener("abort", abortHandler);
	}
}

function validatePartSize(fileName: string, partSize: number): void {
	if (!Number.isSafeInteger(partSize) || partSize <= 0) {
		throw new Error(`Invalid sequential stream part size for "${fileName}": ${partSize}`);
	}
}

/**
 * Splits a stream in one forward-only pass. Every non-final part is exactly partSize bytes; the
 * final part may be shorter, and exact multiples never emit a zero-byte tail.
 */
export async function* splitDriverFileStream(
	file: StreamCapableDriverFile,
	options: SplitDriverFileStreamOptions = {}
): AsyncGenerator<SequentialStreamPart> {
	if (typeof file.stream !== "function") throw new FileUnreadableError(file.name);

	const partSize = options.partSize ?? CHUNK_SIZE;
	const readTimeoutMs = options.readTimeoutMs ?? DEFAULT_STREAM_READ_TIMEOUT_MS;
	validatePartSize(file.name, partSize);
	if (!Number.isFinite(readTimeoutMs) || readTimeoutMs <= 0) {
		throw new Error(`Invalid sequential stream read timeout for "${file.name}": ${readTimeoutMs}`);
	}

	let reader: StreamReader;
	try {
		reader = file.stream().getReader() as unknown as StreamReader;
	} catch (error) {
		if (isReadFailure(error)) throw new FileUnreadableError(file.name);
		throw error;
	}
	const queued: Uint8Array[] = [];
	let queuedBytes = 0;
	let bytesRead = 0;
	let chunkIndex = 0;

	const takePart = (size: number): ArrayBuffer => {
		const output = new Uint8Array(size);
		let outputOffset = 0;
		while (outputOffset < size) {
			const head = queued[0];
			const copiedBytes = Math.min(head.byteLength, size - outputOffset);
			output.set(head.subarray(0, copiedBytes), outputOffset);
			outputOffset += copiedBytes;
			if (copiedBytes === head.byteLength) queued.shift();
			else queued[0] = head.subarray(copiedBytes);
		}
		queuedBytes -= size;
		return output.buffer;
	};

	try {
		while (true) {
			const result = await withPickedFileRead(() => readWithTimeout(reader, file.name, readTimeoutMs, options.signal));
			if (result.done) break;
			const value = toUint8Array(result.value, file.name);
			bytesRead += value.byteLength;
			if (bytesRead > file.size) throw new FileUnreadableError(file.name);
			if (value.byteLength === 0) continue;
			queued.push(value);
			queuedBytes += value.byteLength;
			while (queuedBytes >= partSize) {
				yield { chunkIndex, PartNumber: chunkIndex + 1, buffer: takePart(partSize) };
				chunkIndex++;
			}
		}
	} catch (error) {
		if (isReadFailure(error)) throw new FileUnreadableError(file.name);
		throw error;
	} finally {
		try {
			await reader.cancel?.();
		} catch {
			// Cancellation is best-effort. The source error is the useful failure to the caller.
		}
		reader.releaseLock?.();
	}

	if (bytesRead !== file.size) throw new FileUnreadableError(file.name);
	if (queuedBytes > 0) {
		yield { chunkIndex, PartNumber: chunkIndex + 1, buffer: takePart(queuedBytes) };
	}
}

export async function persistDriverFileStream(
	file: StreamCapableDriverFile,
	options: PersistDriverFileStreamOptions
): Promise<{ chunkCount: number; totalBytes: number }> {
	let chunkCount = 0;
	let totalBytes = 0;
	for await (const part of splitDriverFileStream(file, options)) {
		const persistedPart = { ...part, size: part.buffer.byteLength };
		await options.persistChunk(persistedPart);
		chunkCount++;
		totalBytes += persistedPart.size;
	}
	return { chunkCount, totalBytes };
}

export type { DriverFile, SourceReadStrategy, SourceReadStrategyOptions };
export { selectSourceReadStrategy };
